/**
 * `/setup`'s checklists: the computer's setup steps, and a connect's stages.
 * Each row is done (a check), running (a spinner and "Working", or "In line"
 * while waiting for a computer), failed (with why beneath it) or pending.
 */
import { cn } from "@crew/ui/lib/utils";
import { Check, CircleAlert } from "lucide-react";
import type { ReactNode } from "react";
import type { StepState } from "./onboarding";
import { rowLook, type ChecklistRow, type RowLook } from "./setup-view";

export type { ChecklistRow } from "./setup-view";

/** A track and a turning arc, in the running row's colour. */
function Turning({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={cn("size-4 animate-spin", className)}>
      <circle
        cx="12"
        cy="12"
        r="8.5"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.14"
        strokeWidth="2.4"
      />
      <path
        d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

const ICONS: Record<RowLook["icon"], ReactNode> = {
  done: <Check className="size-4 text-moss" strokeWidth={2.4} />,
  failed: <CircleAlert className="size-4 text-destructive" />,
  running: <Turning className="text-foreground" />,
  queued: <Turning className="text-copper" />,
  pending: <span className="size-2 rounded-full ring-[1.5px] ring-foreground/30 ring-inset" />,
};

const LABEL_TONE: Record<StepState, string> = {
  done: "text-copy",
  running: "font-semibold text-foreground",
  failed: "font-semibold text-foreground",
  pending: "text-hint",
};

/** One row: its icon, label and what it says, with `failure` beneath it when it failed. */
function ChecklistItem({
  row,
  kind,
  failure,
}: {
  row: ChecklistRow;
  kind: "step" | "stage";
  failure: ReactNode;
}) {
  const look = rowLook(row);
  return (
    <li
      {...{ [`data-${kind}`]: row.id }}
      data-state={row.state}
      className="border-t border-border first:border-t-0"
    >
      <div className="flex items-center gap-3 py-2.25">
        <span className="flex size-5 shrink-0 items-center justify-center">{ICONS[look.icon]}</span>
        <span className={cn("grow text-body leading-5", LABEL_TONE[row.state])}>{row.label}</span>
        <span className={cn("w-22.5 shrink-0 text-right text-meta leading-4.5", look.tone)}>
          {look.says}
        </span>
      </div>
      {row.state === "failed" ? (
        <div className="-mt-1.25 flex flex-col gap-3 pb-3.5 pl-8">{failure}</div>
      ) : null}
    </li>
  );
}

/**
 * The rows, with `failure` beneath the failed one. `kind` names each row's
 * data attribute (`data-step` or `data-stage`), beside its `data-state`.
 */
export function Checklist({
  rows,
  kind,
  failure,
}: {
  rows: ChecklistRow[];
  kind: "step" | "stage";
  failure?: ReactNode;
}) {
  return (
    <ol className="flex flex-col">
      {rows.map((row) => (
        <ChecklistItem key={row.id} row={row} kind={kind} failure={failure} />
      ))}
    </ol>
  );
}

/** Why a step or stage failed: its first sentence, then the rest. */
export function FailureBox({ title, detail }: { title: string; detail: string | null }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-0.75 rounded-md bg-destructive/6 px-3.5 py-3 ring-1 ring-destructive/18 ring-inset"
    >
      <p className="text-sm leading-5 font-medium text-foreground">{title}</p>
      {detail ? <p className="text-meta leading-[19px] text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

/** A numbered card on `/setup`: its header, then what's in it. */
export function SetupCard({
  number,
  title,
  aside,
  label,
  raised = false,
  className,
  children,
  ...data
}: {
  number: 1 | 2;
  title: string;
  aside?: ReactNode;
  /** The region's name, when it isn't the title. */
  label?: string;
  raised?: boolean;
  className?: string;
  children?: ReactNode;
  [data: `data-${string}`]: string | undefined;
}) {
  return (
    <section
      aria-label={label ?? title}
      {...data}
      className={cn(
        "flex flex-col rounded-panel bg-card ring-1 ring-border",
        raised && "shadow-[0_8px_24px_rgb(31_42_68/6%)]",
        className,
      )}
    >
      <div className={cn("flex items-baseline gap-2 px-5.5 pt-4.5", children ? "pb-1" : "pb-4.5")}>
        <span className="text-xs leading-4 font-semibold text-hint">{number}</span>
        <h2 className="grow text-body leading-5 font-semibold text-foreground">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A card's footer: a note, and the card's action. */
export function SetupCardFooter({ note, children }: { note: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-b-panel border-t border-border bg-background/50 px-5.5 py-3.5">
      <p className="min-w-0 grow basis-0 text-meta leading-4.5 text-muted-foreground">{note}</p>
      {children}
    </div>
  );
}
